import React, { useState, useEffect } from "react";
import AnchorLink from "react-anchor-link-smooth-scroll";
import { Rnd } from "react-rnd";
import { motion } from "framer-motion";
import { components } from "ComponentRenderer.js";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import FAQ from "components/faqs/SingleCol.js";
import MainFeature1 from "components/features/TwoColWithButton.js";

import {
  Container,
  Content2Xl,
  ContentWithVerticalPadding,
} from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";
import { LogoLink } from "components/headers/light.js";
import { SectionHeading as HeadingBase } from "components/misc/Headings";
import { SectionDescription as DescriptionBase } from "components/misc/Typography";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";

import { ReactComponent as CheckboxIcon } from "feather-icons/dist/icons/check-circle.svg";
import { ReactComponent as RadioIcon } from "feather-icons/dist/icons/radio.svg";
import Hero from "components/hero/TwoColumnWithPrimaryBackground";
import "./main.css";
import heroScreenshotImageSrc from "images/demo/MainLandingPageHero.png";
import logo from "images/logo.png";
import MiniCenteredFooter from "components/footers/MiniCenteredFooter";
import DashedBorderSixFeatures from "components/features/DashedBorderSixFeatures";
import BackgroundAsImage from "components/hero/BackgroundAsImage";
import BackgroundAsImageWithCenteredContent from "components/hero/BackgroundAsImageWithCenteredContent";
import VerticalWithAlternateImageAndText from "components/features/VerticalWithAlternateImageAndText";
/* Hero */
const Row = tw.div`flex`;
const NavRow = tw(Row)`flex flex-col lg:flex-row items-center justify-between`;
const NavLink = tw.a`mt-4 lg:mt-0 transition duration-300 font-medium pb-1 border-b-2 mr-12 text-gray-700 border-gray-400 hocus:border-gray-700`;
const PrimaryNavLink = tw(
  NavLink
)`text-gray-100 bg-blue-500 px-6 py-3 border-none rounded hocus:bg-blue-900 focus:shadow-outline mt-6 md:mt-4 lg:mt-0`;
const HeroRow = tw(
  Row
)`flex-col lg:flex-row justify-between items-center pt-8 lg:pt-12 pb-16 max-w-screen-2xl mx-auto flex-wrap`;
const Subheading = tw.span`uppercase tracking-wider text-sm`;

const Column = tw.div`flex-1`;

const UpdateNoticeIcon = tw(RadioIcon)`w-0 sm:w-5 sm:mr-3`;

const TextColumn = tw(
  Column
)`mx-auto lg:mr-0 max-w-2xl lg:max-w-xl xl:max-w-2xl flex-shrink-0`;
const Heading = tw(
  HeadingBase
)`text-center lg:text-left text-blue-700 leading-snug`;
const Description = tw(
  DescriptionBase
)`mt-4 text-center lg:text-left lg:text-base text-gray-700 max-w-lg mx-auto lg:mx-0`;
const Actions = tw.div`flex flex-col sm:flex-row justify-center lg:justify-start`;
const ActionButton = tw(
  AnchorLink
)`px-8 py-3 font-bold rounded bg-blue-500 text-gray-100 hocus:bg-blue-700 hocus:text-gray-200 focus:shadow-outline focus:outline-none transition duration-300 mt-12 inline-block tracking-wide text-center px-10 py-4 font-semibold tracking-normal`;
const PrimaryButton = tw(ActionButton)``;
const FeatureList = tw.ul`mt-6 leading-loose flex flex-wrap max-w-xl mx-auto lg:mx-0`;
const Feature = tw.li`mt-2 flex items-center flex-shrink-0 w-full sm:w-1/2 justify-center lg:justify-start`;
const FeatureIcon = tw(CheckboxIcon)`w-5 h-5 text-blue-700`;
const FeatureText = tw.p`ml-2 font-medium text-gray-700`;
const ImageColumn = tw(Column)`mx-auto lg:mr-0 relative mt-16 lg:mt-0 lg:ml-8`;
const ImageContainer = tw.div``;
const Image = tw.img`max-w-full rounded-t sm:rounded`;

const SectionContainer = tw(ContentWithVerticalPadding)``;
const SectionHeading = tw(HeadingBase)`text-blue-700`;
const SectionDescription = tw(
  DescriptionBase
)`text-center mx-auto text-gray-900 max-w-4xl`;

const PreviewCards = tw.div`flex flex-wrap -mr-12`;
const PreviewCardContainer = tw.div`mt-24 mx-auto md:mx-0 max-w-lg w-full md:w-1/2 lg:w-1/3 pr-12`;
const PreviewCard = tw(motion.a)`block rounded-lg shadow-raised`;
const PreviewCardImageContainer = tw.div`rounded-t-lg border-0 border-b-0`;
const PreviewCardImage = styled(motion.div)`
  ${(props) =>
    css`
      background-image: url("${props.imageSrc}");
    `}
  ${tw`h-128 md:h-144 bg-cover bg-left-top`}
`;
const PreviewButton = tw(
  PrimaryButtonBase
)`w-full rounded-b-lg rounded-t-none py-5 font-semibold`;

const ResizableBox = styled(Rnd)`
  ${tw`relative! bg-white pr-4`}
  .resizeHandleWrapper > div {
    ${tw`w-4! right-0!`}
  }
`;

export default ({
  features = null,
  primaryButtonUrl = "#landingPageDemos",
  primaryButtonText = "Explore Designs",
  buttonRoundedCss = "",
  landingPages = components.landingPages,
  innerPages = components.innerPages,
  blocks = components.blocks,
  heading = "Toronto Web Development Company",
  description = "We create elegantly designed websites ussing moderm web development techniques. Our sites are easily customizable to fit any theme and industry.",
}) => {
  /*
   * Using gtag like this because we only want to use Google Analytics when Main Landing Page is rendered
   * Remove this part and the the gtag script inside public/index.html if you dont need google analytics
   */
  window.gtag("js", new Date());
  window.gtag("config", "UA-45799926-9");

  const previewImageAnimationVariants = {
    rest: {
      backgroundPositionY: "0%",
    },
    hover: {
      backgroundPositionY: "100%",
      transition: { type: "tween", ease: "linear", duration: 5 },
    },
  };

  const noOfComponentBlocks = Object.values(blocks).reduce(
    (acc, block) => acc + Object.keys(block.elements).length,
    0
  );

  // features = features || [
  //   `Modern Designs`,
  //   `Custom Built Websites`,
  //   `SEO and Marketing`,
  //   "Website Maintenance",
  //   "User Authentication",
  //   "Logo Design",
  // ];

  return (
    <AnimationRevealPage enabled>
      <div className="page">
        <Container tw="bg-gray-100 -mx-8 -mt-8 pt-8 px-0">
          {/* <BackgroundAsImageWithCenteredContent /> */}
          <Content2Xl>
            <MainFeature1
              subheading={<Subheading>Sports News</Subheading>}
              heading="Raptors Training Camp"
              buttonRounded={false}
              primaryButtonText="Read More"
              primaryButtonUrl="https://www.raptorshq.com/2022/6/30/23189479/nba-free-agency-2022-toronto-raptors-what-to-expect-chris-boucher-fred-vanvleet"
              description="The transition from June into July is always a hectic one in the NBA, with the NBA Finals wrapping up, the NBA Draft, then the start of NBA free agency.

But for the Toronto Raptors, this year seems like a quiet one."
              imageSrc="https://cdn.vox-cdn.com/thumbor/a2Dpwo6JjpZCKoRjCZOVW90Vl4c=/0x0:3680x2453/920x613/filters:focal(1319x411:1907x999):format(webp)/cdn.vox-cdn.com/uploads/chorus_image/image/71010926/1387574555.0.jpg"
            />
            <MainFeature1
              subheading={<Subheading>Sports News</Subheading>}
              heading="Maple Leafs Schedule"
              buttonRounded={false}
              description="The Toronto Maple Leafs announced today the club's 2022 NHL preseason schedule, which begins with a doubleheader with two games against the Ottawa Senators on Saturday, September 24."
              primaryButtonText="Read More"
              primaryButtonUrl="https://www.nhl.com/mapleleafs/news/maple-leafs-announce-2022-preseason-schedule/c-334790310"
              imageSrc="https://cms.nhl.bamgrid.com/images/photos/334790366/1024x576/cut.png"
              textOnLeft={false}
            />

            <SectionContainer id="landingPageDemos">
              <SectionHeading>Your Teams</SectionHeading>
              <SectionDescription>
                Raptors | Blue Jays | Maple Leafs
              </SectionDescription>
              <PreviewCards>
                {Object.entries(landingPages).map(([pageName, page], index) => (
                  <PreviewCardContainer key={index}>
                    <PreviewCard
                      initial="rest"
                      animate="rest"
                      whileHover="hover"
                      href={page.url}
                    >
                      <PreviewCardImageContainer>
                        <PreviewCardImage
                          transition={{ type: "tween" }}
                          variants={previewImageAnimationVariants}
                          imageSrc={page.imageSrc}
                        />
                      </PreviewCardImageContainer>
                    </PreviewCard>
                  </PreviewCardContainer>
                ))}
              </PreviewCards>
            </SectionContainer>
            <VerticalWithAlternateImageAndText />
          </Content2Xl>
        </Container>
      </div>
    </AnimationRevealPage>
  );
};

// const BlocksRenderer = ({ blocks }) => {
//   const [lastVisibleBlockIndex, setLastVisibleBlockIndex] = useState(0);

//   const updateLastVisibleBlockIndex = (index) => {
//     console.log("LAST WAS ", lastVisibleBlockIndex);
//     if (index > lastVisibleBlockIndex) setLastVisibleBlockIndex(index);
//   };

//   return (
//     <ComponentsContainer>
//       {blocks.map(
//         (block, index) =>
//           lastVisibleBlockIndex + 1 >= index && (
//             <Block
//               key={index}
//               components={block}
//               notifyIsVisible={() => updateLastVisibleBlockIndex(index)}
//             />
//           )
//       )}
//     </ComponentsContainer>
//   );
// };

// const Block = ({ notifyIsVisible, components }) => {
//   const offset = 30;
//   const [ref, inView] = useInView(offset);

//   useEffect(() => {
//     if (inView) notifyIsVisible();
//   }, [inView, notifyIsVisible]);

//   const ResizeHandle = (
//     <ResizeHandleButton>
//       <HandleIcon tw="w-4 h-4 text-gray-600" />
//     </ResizeHandleButton>
//   );

//   const componentBlockRefs = {};

//   const updateComponentBlockIframeHeight = (iframe) => {
//     iframe.style.height = "auto";
//     iframe.style.height =
//       iframe.contentWindow.document.body.scrollHeight + "px";
//   };

//   return (
//     <div ref={ref} tw="mt-32">
//       <ComponentsType>{components.type}</ComponentsType>
//       <Components>
//         {Object.values(components.elements).map((component, componentIndex) => (
//           <Component key={componentIndex}>
//             <ComponentHeading>
//               <ComponentName>{component.name}</ComponentName>
//               <ComponentPreviewLink
//                 className="group"
//                 href={component.url}
//                 target="_blank"
//               >
//                 View Live Demo{" "}
//                 <ArrowRightIcon tw="transition duration-300 transform group-hover:translate-x-px ml-2 w-4 h-4" />
//               </ComponentPreviewLink>
//             </ComponentHeading>
//             <ComponentContent>
//               <ResizableBox
//                 minWidth={310}
//                 default={{
//                   width: "100%",
//                   height: "100%",
//                 }}
//                 bounds="parent"
//                 disableDragging={true}
//                 enableResizing={{ right: true }}
//                 resizeHandleComponent={{ right: ResizeHandle }}
//                 resizeHandleWrapperClass={`resizeHandleWrapper`}
//                 onResize={() =>
//                   updateComponentBlockIframeHeight(
//                     componentBlockRefs[component.url]
//                   )
//                 }
//               >
//                 <iframe
//                   src={component.url}
//                   title="Hero"
//                   width="100%"
//                   ref={(ref) => (componentBlockRefs[component.url] = ref)}
//                   onLoad={(e) => updateComponentBlockIframeHeight(e.target)}
//                 />
//               </ResizableBox>
//             </ComponentContent>
//           </Component>
//         ))}
//       </Components>
//     </div>
//   );
// };
